import React, { useEffect, useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About us", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Products", path: "/Products" },
    {
      name: "Industries",
      path: "/industries",
      hasDropdown: true,
      dropdownItems: [
        { name: "Electronic Packaging", path: "/industries/electronic" },
        { name: "Food", path: "/industries/food" },
        { name: "Pharmaceutical", path: "/industries/pharmaceutical" },
        { name: "Industrial", path: "/industries/industrial" },
        { name: "Construction", path: "/industries/construction" },
        { name: "Decorative & Event", path: "/industries/decorative-event" },
      ],
    },
    { name: "Contact us", path: "/contact" },
  ];

  const handleNavClick = () => {
    setMenuOpen(false);
    setOpenMobileDropdown(null);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0); // 100vh
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDesktopDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed flax top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-md" : "bg-transparent"
      }`}
    >
      <div className="section-container py-3 flex items-center justify-between">
        <NavLink to="/" className="flex items-center space-x-2">
          <img
            src={`/assets/logo.png`}
            alt="Logo"
            className="h-16 w-auto object-contain"
          />
        </NavLink>

        <ul className="hidden md:flex space-x-10 text-base text-primary-200">
          {navLinks.map((link, idx) => (
            <li key={idx} className="relative group">
              {!link.hasDropdown ? (
                <NavLink
                  to={link.path}
                  className={({ isActive }) =>
                    `relative group flex items-center space-x-1 cursor-pointer text-base font-semibold transition-all duration-200 hover:underline-primary hover:text-primary-gradient
                      ${isActive ? "text-primary-gradient underline-primary" : "text-primary-200 after:w-0" }
                      after:absolute after:-bottom-1 after:left-1/2 after:-translate-x-1/2
                      after:transition-all after:duration-300`
                  }
                >
                  {link.name}
                </NavLink>
              ) : (
                <div
                  className="relative cursor-pointer group"
                  ref={dropdownRef}
                  onClick={() => setOpenDesktopDropdown((prev) => !prev)}
                >
                  <span className="flex items-center space-x-1 text-primary-200 font-semibold">
                    {link.name}
                    {openDesktopDropdown ? (
                      <IoIosArrowUp className="w-4 h-4" />
                    ) : (
                      <IoIosArrowDown className="w-4 h-4" />
                    )}
                  </span>
                  <div
                    className={`absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded shadow-lg transition-all duration-300 z-50
                    ${openDesktopDropdown ? "opacity-100 visible" : "opacity-0 invisible"}`}
                  >
                    <ul className="py-2">
                      {link.dropdownItems.map((item, subIdx) => (
                        <li key={subIdx}>
                          <NavLink
                            to={item.path}
                            className="block px-4 py-2 text-sm text-primary-200 hover:bg-gray-100"
                          >
                            {item.name}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>

        {/* Mobile Icon */}
        <button
          className="md:hidden text-primary-200 transition-all duration-300 hover:scale-110"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-50 flex">
          {/* Blur Background */}
          <div
            className="absolute inset-0 bg-black/10 backdrop-blur-sm"
            onClick={handleNavClick}
          ></div>

          {/* Sliding Sidebar (Right to Left) */}
          <div
            className={`ml-auto w-[70%] h-full bg-white p-4 overflow-y-auto shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
              menuOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <NavLink to="/" onClick={handleNavClick}>
                <img
                  src={`/assets/logo.png`}
                  alt="Logo"
                  className="h-12 w-auto object-contain"
                />
              </NavLink>
              <button className="text-primary-100" onClick={handleNavClick}>
                <FaTimes size={24} />
              </button>
            </div>

            <div className="space-y-2 text-sm font-medium">
              {navLinks.map((link, idx) => (
                <div key={idx} className="border-b border-gray-200 pb-2">
                  {!link.hasDropdown ? (
                    <NavLink
                      to={link.path}
                      onClick={handleNavClick}
                      className={({ isActive }) =>
                        `block text-primary-200 py-2 ${isActive ? "font-semibold" : ""}`
                      }
                    >
                      {link.name}
                    </NavLink>
                  ) : (
                    <>
                      <div
                        className="flex justify-between items-center py-2 cursor-pointer"
                        onClick={() =>
                          setOpenMobileDropdown((prev) =>
                            prev === link.name ? null : link.name
                          )
                        }
                      >
                        <span className="text-primary-200">{link.name}</span>
                        {openMobileDropdown === link.name ? (
                          <IoIosArrowUp className="transition-transform" />
                        ) : (
                          <IoIosArrowDown className="transition-transform" />
                        )}
                      </div>

                      {openMobileDropdown === link.name && (
                        <ul className="pl-4 text-sm space-y-1">
                          {link.dropdownItems.map((item, subIdx) => (
                            <li key={subIdx}>
                              <NavLink
                                to={item.path}
                                onClick={handleNavClick}
                                className="block py-1 text-primary-200 hover:text-primary-100"
                              >
                                {item.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
